import { Box, SegmentedControl, SimpleGrid, Stack, Text, UnstyledButton } from "@mantine/core";
import { useMediaQuery } from "@mantine/hooks";
import { useTranslation } from "react-i18next";

import { useAppearance } from "../../theme/AppearanceContext";
import { APP_THEMES, DEFAULT_THEME } from "../../theme/themes";
import { MOTIONS, TABLE_MODES, THEME_IDS, type Motion, type TableMode } from "../../theme/prefs";

export function AppearanceSettingsTab() {
  const { t } = useTranslation();
  const { theme, setTheme, tableMode, setTableMode, motion, setMotion } = useAppearance();
  const narrow = useMediaQuery("(max-width: 48em)");

  return (
    <Stack gap="lg">
      <Stack gap="xs">
        <Text fw={600}>{t("appearance.theme")}</Text>
        <SimpleGrid cols={{ base: 2, sm: 3, md: 5 }} spacing="sm">
          {THEME_IDS.map((id) => {
            const sw = APP_THEMES[id].swatch;
            const active = id === theme;
            return (
              <UnstyledButton
                key={id}
                onClick={() => setTheme(id)}
                aria-pressed={active}
                aria-label={t(`theme.${id}`)}
                style={{
                  borderRadius: 10,
                  padding: 6,
                  background: sw.bg,
                  border: `2px solid ${active ? sw.accent : "rgba(128,128,128,.35)"}`,
                  boxShadow: active ? `0 0 0 2px ${sw.accent}33` : undefined,
                }}
              >
                <Box style={{ height: 10, borderRadius: 4, background: sw.accent, marginBottom: 6 }} />
                <Box style={{ height: 12, borderRadius: 4, background: sw.surface, marginBottom: 4 }} />
                <Box style={{ height: 12, borderRadius: 4, background: sw.surface, marginBottom: 6 }} />
                <Text size="xs" fw={active ? 700 : 500} c={sw.accent} ta="center" lh={1.2}>
                  {t(`theme.${id}`)}
                  {id === DEFAULT_THEME ? ` (${t("theme.default")})` : ""}
                </Text>
              </UnstyledButton>
            );
          })}
        </SimpleGrid>
      </Stack>

      <Stack gap="xs">
        <Text fw={600}>{t("appearance.tableMode")}</Text>
        <SegmentedControl
          fullWidth={!!narrow}
          value={tableMode}
          onChange={(v) => setTableMode(v as TableMode)}
          data={TABLE_MODES.map((m) => ({
            value: m,
            label: t(`appearance.tableMode${m[0].toUpperCase()}${m.slice(1)}`),
          }))}
        />
      </Stack>

      <Stack gap="xs">
        <Text fw={600}>{t("appearance.motion")}</Text>
        <SegmentedControl
          fullWidth={!!narrow}
          value={motion}
          onChange={(v) => setMotion(v as Motion)}
          data={MOTIONS.map((m) => ({
            value: m,
            label: t(m === "on" ? "appearance.motionOn" : "appearance.motionReduced"),
          }))}
        />
      </Stack>

      <Text size="sm" c="dimmed">
        {t("appearance.perBrowser")}
      </Text>
    </Stack>
  );
}
